/* eslint-disable no-underscore-dangle */
import axios from 'axios';
import { getAuthTokens } from '../domain/auth/authUseCases';

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_URL_ADMIN_V2,
  timeout: 5000,
});

/**
 * Axios interceptor to pass the JWT token if exists
 * @param {import('axios').InternalAxiosRequestConfig} axiosConfig
 * @returns {import('axios').InternalAxiosRequestConfig}
 */
const onRequest = (axiosConfig) => {
  // Try to get token from auth context first, then fall back to temp download token
  let token = getAuthTokens()?.token;

  // If no auth token, check for temporary download token
  if (!token) {
    token = window.localStorage.getItem('temp_download_token');
  }

  const newConfig = { ...axiosConfig };
  if (token && axiosConfig.url !== '/update-password') {
    newConfig.headers.Authorization = `Bearer ${token}`;
  }

  return newConfig;
};

/**
 * Refresh the access token
 * @param {import('axios').AxiosError & { config: { _retry?: boolean }}} axiosError
 * @returns {Promise<import('axios').AxiosResponse|import('axios').AxiosError>}
 */
const refreshAccessToken = async (axiosError) => {
  const originalRequest = axiosError.config;

  // If using temporary download token and get 401, don't try to refresh
  const hasTempToken = window.localStorage.getItem('temp_download_token');
  if (hasTempToken && !getAuthTokens()?.token) {
    // Remove invalid temp token
    localStorage.removeItem('temp_download_token');
    return Promise.reject(axiosError.response.data);
  }

  if (axiosError.response
    && axiosError.response.status === 401
    && !originalRequest._retry
    && originalRequest.url !== '/token/refresh'
  ) {
    originalRequest._retry = true;

    try {
      const response = await instance.post('/token/refresh', { refresh_token: getAuthTokens().refresh_token });

      const newAccessToken = response.data.token;
      localStorage.setItem('auth', JSON.stringify(response.data));
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
      return axios(originalRequest);
    } catch (refreshError) {
      if (window.location.pathname !== '/login') {
        window.location.replace('/login');
        localStorage.removeItem('auth');
      }
    }
  }
  if (axiosError.response
    && axiosError.response.status === 401
    && originalRequest.url === '/token/refresh') {
    localStorage.removeItem('auth');
  }

  return Promise.reject(axiosError.response.data);
};

/**
 * Axios interceptor to manage API response
 * @param {import('axios').AxiosError<any, any> & { config: { _retry?: boolean; }; }} error
 * @returns {Promise<any>}
 */
const onRejected = (error) => refreshAccessToken(error);

instance.interceptors.request.use(onRequest);
instance.interceptors.response.use((res) => res, onRejected);

export default instance;
